/**
 * Site-wide measurement privacy and routing policy.
 *
 * This module is intentionally the only place where application analytics
 * accepts values that came from the browser. Event producers pass semantic
 * identifiers, never visitor-supplied text, URLs, form values or DOM labels.
 */
import sitemapRoutes from "@/sitemap-routes.json";
import campaignValues from "@/data/measurementCampaigns.json";

export const TRACKING_SCHEMA_VERSION = "site_measurement_v1";

const SITE_PATHS = new Set([
  ...sitemapRoutes.map(({ path }) => path),
  "/blog",
  "/thank-you-for-signing-up",
  "/accept-quote",
  "/landing/partner-programme",
  "/landing/stripe-cost-check",
  "/404",
  "/payments-beyond-purpose",
  "/payments-with-purpose",
  "/partners/integrations/woocommerce",
  "/partners/integrations/shopify",
  "/partners/integrations/magento",
  "/partners/integrations/prestashop",
  "/partners/integrations/bigcommerce",
  "/partners/integrations/opencart",
  "/partners/integrations/drupal",
]);

const FORM_NAMES = new Set([
  "contact_general", "product_enquiry", "epos_demo", "referral",
  "lead_popup_health_check", "callback_request", "partner_application",
  "partner_enquiry", "court_connect_partner", "stripe_cost_check",
  "statement_review", "tool_overpaying", "tool_hidden_fees",
  "tool_cash_vs_card", "tool_switch_vs_stay", "tool_cost_of_cash",
  "tool_savings", "quiz_payment_finder", "quote_in_person", "quote_online",
]);

const ITEM_NAMES = new Set([
  "card_machines", "epos", "online_payments", "phone_payments", "online_ordering",
  "merchant_cash_advance", "payment_solution_finder", "savings_calculator",
  "overpaying_scorecard", "hidden_fees_checker", "cost_of_cash_calculator",
  "in_person_quote", "online_quote", "cash_vs_card_comparator",
  "switch_vs_stay_calculator", "statement_review_upload", "switch_and_save",
  "partner_programme", "epos_partner_calculator", "ecommerce_partner_calculator",
  "ecommerce_integrations", "refer_a_friend", "partner_programme_landing",
  "court_connect_partner_landing", "partner_programme_application",
  "court_connect_partner_enquiry", "work_with_us_section",
  "pax_a920_pro", "ingenico_dx8000", "ingenico_move_5000", "ingenico_desk_5000",
  "verifone_t650p", "elavon_mobile", "paypal_reader",
  "epos_register", "payment_gateway", "card_machine_pax_a920_pro",
  "card_machine_ingenico_dx8000", "card_machine_ingenico_move_5000",
  "card_machine_ingenico_desk_5000", "card_machine_verifone_t650p",
  "card_machine_elavon_mobile", "card_machine_paypal_reader", "card_machine_unknown",
  "review_filter_all", "review_filter_google", "review_filter_trustpilot",
  "review_source_google", "review_source_trustpilot",
  "review_tag_industry_hospitality", "review_tag_industry_retail",
  "review_tag_industry_services", "review_tag_industry_e_commerce",
  "review_tag_industry_fitness", "review_tag_industry_pet_services",
  "review_tag_industry_beauty", "review_tag_industry_partnership",
  "review_tag_product_card_machines", "review_tag_product_payment_gateway",
  "review_tag_product_epos_systems", "review_tag_product_online_payments",
  "review_tag_product_online_ordering", "review_tag_product_virtual_terminal",
  "review_tag_product_merchant_cash_advance", "review_tag_product_partners",
]);

const ITEM_TYPES = new Set([
  "service_product", "calculator", "finder", "quote_tool", "comparison", "upload",
  "partner_service", "partner_programme", "partner_calculator", "event_landing",
  "social_proof", "review_filter", "review_card", "review_tag",
]);
const ITEM_CATEGORIES = new Set(["product", "tool", "quote", "upload", "partner", "social_proof"]);
const FUNNEL_STAGES = new Set(["interest", "consideration", "evaluation", "conversion"]);
const SUBMISSION_METHODS = new Set(["form", "email", "upload"]);
const FORM_STEP_IDS = new Set([
  "step_1", "step_2", "step_3", "step_4", "step_5",
  "step_6", "step_7", "step_8", "step_9", "step_10",
]);
const FORM_STEP_STATES = new Set(["next", "back"]);
const LINK_ROLES = new Set(["internal_navigation", "outbound", "download", "anchor"]);
const COMPONENT_IDS = new Set(["global_navigation", "global_footer", "sticky_quote_bar", "sitewide_contact", "page_content"]);
const CONTACT_METHODS = new Set(["phone", "email", "whatsapp"]);
const CTA_IDS = new Set(["card-machines-free-statement-review", "card-machines-how-pricing-works"]);
const REVIEW_FILTERS = new Set(["all", "google", "trustpilot"]);
const REVIEW_SOURCES = new Set(["google", "trustpilot"]);
const REVIEW_TAGS = new Set([
  "industry:hospitality", "industry:retail", "industry:services", "industry:e-commerce",
  "industry:fitness", "industry:pet-services", "industry:beauty", "industry:partnership",
  "product:card-machines", "product:payment-gateway", "product:epos-systems",
  "product:online-payments", "product:online-ordering", "product:virtual-terminal",
  "product:merchant-cash-advance", "product:partners",
]);
const WHATSAPP_PLACEMENTS = new Set([
  "float", "footer", "navbar", "navbar_mobile", "home_support", "contact", "statement",
  "pricing", "card_machines", "epos", "online", "thank_you_signup",
]);
const LANDING_EVENTS = new Set([
  "scc_landing_view", "scc_stripe_pricing_click", "scc_fees_guide_click", "scc_cta_click", "scc_how_it_works_click",
  "scc_mobile_sticky_cta_click", "scc_form_start", "scc_form_submit", "scc_form_success",
  "scc_form_error", "partner_apply_click", "partner_discovery_click",
  "partner_zoho_fallback_click", "partner_mobile_sticky_apply_click",
  "partner_scroll_25", "partner_scroll_50", "partner_scroll_75", "partner_scroll_90",
  "partner_discovery_section_view", "partner_zoho_iframe_load", "partner_form_start",
  "partner_form_submit", "partner_form_error", "partner_calculator_interaction",
  "partner_calculator_apply_click", "cc_meet_team_click", "cc_reviews_click",
  "cc_programme_click", "cc_partner_cta_click", "cc_mobile_sticky_cta_click",
  "cc_partner_form_start", "cc_partner_form_submit", "cc_partner_form_error",
]);

// The shared dataLayer boundary only accepts this finite contract. GTM's
// nested-object data model means an unexpected event can otherwise inherit
// earlier context even when its source component supplied no parameters.
const EVENT_NAMES = new Set([
  "page_view",
  "form_start", "form_submit", "form_validation_error", "form_request_error", "form_step", "generate_lead",
  "popup_shown", "popup_dismissed",
  "click_phone", "click_email", "click_cta", "click_whatsapp", "click_navigation",
  "email_capture", "calculator_result", "booking_submit", "scroll_depth", "view_service_page",
  "consent_update", "guide_link_click",
  "product_viewed", "product_clicked",
  "tool_opened", "tool_started", "tool_completed",
  "quote_requested", "email_quote_requested", "statement_file_selected",
  "statement_transfer_success", "statement_transfer_error", "lead_submitted",
  "partner_section_viewed", "partner_clicked", "partner_tool_opened", "partner_tool_started",
  "partner_tool_completed", "partner_lead_started", "partner_lead_submitted",
  "review_filter_selected", "review_card_clicked", "review_tag_clicked",
  ...LANDING_EVENTS,
]);

// These identities were evidenced for the production and isolated TEST
// destinations. Do not make a hostname production-eligible through a public
// build variable: GTM routing must be reviewed separately for every new host.
const MEASUREMENT_HOSTS = new Set([
  "candopayments.com",
  "www.candopayments.com",
  "d2hqz144sz4ut1.cloudfront.net",
]);
// Referrers are source metadata, not free-form URLs. Keep only known public
// acquisition origins; everything else is blank rather than risking a
// visitor-specific subdomain or an unreviewed attribution classification.
const ACQUISITION_REFERRER_HOSTS = new Set([
  "google.com", "www.google.com", "google.co.uk", "www.google.co.uk",
  "bing.com", "www.bing.com", "duckduckgo.com", "www.duckduckgo.com",
  "yahoo.com", "search.yahoo.com", "uk.search.yahoo.com",
  "facebook.com", "www.facebook.com", "m.facebook.com", "l.facebook.com", "lm.facebook.com",
  "instagram.com", "www.instagram.com", "l.instagram.com",
  "linkedin.com", "www.linkedin.com", "t.co", "x.com", "www.x.com", "twitter.com", "www.twitter.com",
  "chatgpt.com", "chat.openai.com", "perplexity.ai", "www.perplexity.ai", "copilot.microsoft.com",
]);

// These are finite acquisition classifications used by the established form
// attribution contract. Campaign/content/term values remain generated from
// the canonical measurement registry in measurementCampaigns.json.
const UTM_SOURCES = new Set([
  "google", "google_ads", "meta", "facebook", "instagram", "linkedin",
  "gbp", "google_business", "email", "partner", "organic",
  // Elavon referral campaign, exact observed label (Gary, 7 Oct 2026).
  "Website",
]);
const UTM_MEDIA = new Set([
  "organic", "cpc", "paid_social", "social", "organic_social", "email",
  "referral", "partner",
  // Elavon referral campaign, exact observed label (Gary, 7 Oct 2026).
  "directory-page",
]);

function valueFrom(set, value) {
  return typeof value === "string" && set.has(value) ? value : "";
}

function browserLocation() {
  return typeof window === "undefined" ? null : window.location;
}

// Test fixtures and non-browser callers sometimes provide only an origin and
// pathname. Prefer the full current URL whenever it is available so relative
// and fragment-only links retain their current page context.
function locationOrigin(location) {
  if (typeof location?.origin === "string" && location.origin) return location.origin;
  try {
    return new URL(location?.href || "").origin;
  } catch (_err) {
    return "";
  }
}

function locationBaseHref(location) {
  if (typeof location?.href === "string" && location.href) return location.href;
  const origin = locationOrigin(location);
  if (!origin) return "";
  const pathname = typeof location?.pathname === "string" && location.pathname.startsWith("/")
    ? location.pathname
    : "/";
  const search = typeof location?.search === "string" ? location.search : "";
  const hash = typeof location?.hash === "string" ? location.hash : "";
  return `${origin}${pathname}${search}${hash}`;
}

/** Unknown hosts never load GTM or push data into its dataLayer. */
export function isMeasurementHostAllowed(location = browserLocation()) {
  if (!location) return false;
  // Jest has no network destination. This preserves deterministic contract
  // tests without making localhost a browser measurement destination.
  if (process.env.NODE_ENV === "test" && location === browserLocation()) return true;
  return MEASUREMENT_HOSTS.has(String(location.hostname || "").toLowerCase());
}

export function isKnownSitePath(value) {
  return typeof value === "string" && SITE_PATHS.has(value);
}

// Production serves every route with or without one trailing slash
// (/pricing and /pricing/ return the same prerendered page), so a single
// trailing slash is the same page. It is measured under the registered path
// without the slash. Anything else (//, unknown routes) still fails closed.
function canonicalSitePath(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/") && !pathname.endsWith("//")) return pathname.slice(0, -1);
  return pathname;
}

/** A CanDo route only, with query and fragment removed. Unknown routes fail closed. */
export function sanitiseSitePath(value, location = browserLocation()) {
  if (!location || typeof value !== "string") return "";
  try {
    const origin = locationOrigin(location);
    const url = new URL(value, locationBaseHref(location));
    const path = canonicalSitePath(url.pathname);
    if (url.origin !== origin || !isKnownSitePath(path)) return "";
    return path;
  } catch (_err) {
    return "";
  }
}

/**
 * External referrers are reduced to one approved public acquisition origin.
 * Path, query, fragments, internal aliases and unrecognised hosts are blank.
 */
export function sanitiseExternalReferrer(value, location = browserLocation()) {
  if (!location || typeof value !== "string") return "";
  try {
    const url = new URL(value);
    const origin = location.origin || new URL(location.href).origin;
    const host = url.hostname.toLowerCase();
    if (!/^https?:$/.test(url.protocol) || url.origin === origin || MEASUREMENT_HOSTS.has(host) || !ACQUISITION_REFERRER_HOSTS.has(host)) return "";
    return url.origin;
  } catch (_err) {
    return "";
  }
}

/**
 * Returns the finite page context that can enter a measurement payload.
 *
 * `canonicalPath` is reserved for a rendered, semantic page identity. The
 * only current caller is the NotFoundPage marker, which represents an
 * intentional 404 response without exporting the unmatched requested URL.
 * It must still be a registered site path. Arbitrary caller paths continue
 * through the normal fail-closed sanitizer.
 */
export function safePageContext(location = browserLocation(), { canonicalPath = "" } = {}) {
  const path = isKnownSitePath(canonicalPath)
    ? canonicalPath
    : sanitiseSitePath(location?.href || "", location);
  const origin = location ? locationOrigin(location) : "";
  return {
    // Keep the field name stable for the GTM contract, but never transmit a
    // query string, fragment or visitor-specific title.
    page_url: path && location ? `${origin}${path}` : "",
    page_path: path,
    page_title: "",
  };
}

/**
 * A page-view-only campaign URL. It keeps a finite, documented manual UTM
 * set and drops every other query key. Callers decide whether the current
 * document is consent-eligible; ordinary event page context stays query-free.
 */
export function safeCampaignPageUrl(location = browserLocation()) {
  const page = safePageContext(location);
  if (!page.page_url || !location) return "";
  try {
    const url = new URL(locationBaseHref(location));
    const host = url.hostname.toLowerCase();
    const testLocation = process.env.NODE_ENV === "test" && location === browserLocation();
    if ((url.protocol !== "https:" && !testLocation) || url.username || url.password || (!MEASUREMENT_HOSTS.has(host) && !testLocation)) return page.page_url;
    // Query text is never passed through. A malformed or overlong query
    // becomes the clean canonical URL instead.
    if (url.search.length - 1 > 1000) return page.page_url;
    const allowed = {
      utm_source: UTM_SOURCES,
      utm_medium: UTM_MEDIA,
      utm_campaign: new Set(campaignValues.utm_campaign || []),
      utm_content: new Set(campaignValues.utm_content || []),
      utm_term: new Set(campaignValues.utm_term || []),
    };
    const values = new URLSearchParams(url.search);
    const output = new URLSearchParams();
    const seen = new Set();
    for (const [key, value] of values.entries()) {
      if (!Object.prototype.hasOwnProperty.call(allowed, key)) continue;
      if (seen.has(key)) return page.page_url;
      seen.add(key);
      if (!allowed[key].has(value)) continue;
      output.set(key, value);
    }
    const query = output.toString();
    return query ? `${page.page_url}?${query}` : page.page_url;
  } catch (_err) {
    return page.page_url;
  }
}

export function safeFormName(value) { return valueFrom(FORM_NAMES, value); }
export function safeItemName(value) { return valueFrom(ITEM_NAMES, value); }
export function safeItemType(value) { return valueFrom(ITEM_TYPES, value); }
export function safeItemCategory(value) { return valueFrom(ITEM_CATEGORIES, value); }
export function safeFunnelStage(value) { return valueFrom(FUNNEL_STAGES, value); }
export function safeSubmissionMethod(value) { return valueFrom(SUBMISSION_METHODS, value); }
export function safeFormStepId(value) { return valueFrom(FORM_STEP_IDS, value); }
export function safeFormStepState(value) { return valueFrom(FORM_STEP_STATES, value); }
export function safeLandingEvent(value) { return valueFrom(LANDING_EVENTS, value); }
export function isMeasurementEventAllowed(value) { return typeof value === "string" && EVENT_NAMES.has(value); }
export function safeWhatsAppPlacement(value) { return valueFrom(WHATSAPP_PLACEMENTS, value); }
export function safeCtaId(value) { return valueFrom(CTA_IDS, value); }
export function safeReviewFilterName(value) {
  return REVIEW_FILTERS.has(value) ? `review_filter_${value}` : "";
}
export function safeReviewSourceName(value) {
  return REVIEW_SOURCES.has(value) ? `review_source_${value}` : "";
}
export function safeReviewTagName(type, value) {
  const key = `${type}:${value}`;
  return REVIEW_TAGS.has(key) ? `review_tag_${key.replace(/[:-]/g, "_")}` : "";
}

export function sanitiseLinkEvent({ componentId, href, linkRole, destinationType } = {}) {
  const role = valueFrom(LINK_ROLES, linkRole);
  const component = valueFrom(COMPONENT_IDS, componentId);
  if (!role || !component) return null;
  const path = role === "internal_navigation" || role === "anchor" ? sanitiseSitePath(href) : "";
  if ((role === "internal_navigation" || role === "anchor") && !path) return null;
  return {
    component_id: component,
    link_role: role,
    destination_type: valueFrom(new Set(["internal", "external", "download", "anchor"]), destinationType),
    destination_path: path,
  };
}

export function sanitiseContactEvent(method, componentId = "sitewide_contact") {
  const contactMethod = valueFrom(CONTACT_METHODS, method);
  const component = valueFrom(COMPONENT_IDS, componentId);
  return contactMethod && component ? { contact_method: contactMethod, component_id: component } : null;
}

/**
 * Common structured-event payload. It accepts only finite semantic values and
 * explicitly drops arbitrary extras, free text, URLs and financial inputs.
 */
export function sanitiseStructuredEvent({ itemName, itemCategory, itemType, source, submissionMethod, funnelStage, extra } = {}) {
  const location = browserLocation();
  const sourcePath = sanitiseSitePath(source || location?.href || "", location);
  const output = {
    item_name: safeItemName(itemName),
    item_category: safeItemCategory(itemCategory),
    item_type: safeItemType(itemType),
    source_path: sourcePath,
    submission_method: safeSubmissionMethod(submissionMethod),
    funnel_stage: safeFunnelStage(funnelStage),
    ...safePageContext(location),
    tracking_schema_version: TRACKING_SCHEMA_VERSION,
  };
  // The only current structured numeric field is a bounded file-selection
  // count. Merchant turnover, commission, results and any other inputs are
  // deliberately not measurement data.
  if (Number.isInteger(extra?.file_count) && extra.file_count >= 1 && extra.file_count <= 5) {
    output.file_count = extra.file_count;
  }
  return output;
}

export function trackingSchemaVersion() { return TRACKING_SCHEMA_VERSION; }
